// Transport shim — maps every `CaptureMutation` kind to ONE fetch
// against the Encore `capture` service per the contracts in
// `docs/buildspec/capture/10-api.md §5.3–§5.8`. Kept apart from the
// queueing + retry state machine in `capture-client.ts` so the
// hot-path endpoint mapping stays easy to scan against the API spec
// and so the unit-testable slice of the adapter has a single public
// entry point (`dispatchToServer`).

import type {
  BuildingStatus,
  CaptureItemRow,
  SessionSummary,
  SpaceWithItems,
} from '@citycare/shared'

import {
  ApiError,
  apiFetch as defaultApiFetch,
  type ApiFetchInit,
} from '../../hooks/api'
import { canonicalizeForUpload } from '../../hooks/powersync/quantity-canonical'

import type {
  CaptureMutation,
  CaptureMutationResult,
} from './capture-client.types'

export type ApiFetchImpl = <T>(path: string, init: ApiFetchInit) => Promise<T>

export function getDefaultApiFetch(): ApiFetchImpl {
  return defaultApiFetch as ApiFetchImpl
}

export async function dispatchToServer(
  mutation: CaptureMutation,
  fetchImpl: ApiFetchImpl,
): Promise<CaptureMutationResult> {
  switch (mutation.kind) {
    case 'updateItem': {
      const item = await fetchImpl<CaptureItemRow>(
        `/capture/items/${encodeURIComponent(mutation.itemId)}`,
        {
          method: 'PUT',
          json: {
            quantity: canonicalizeForUpload(mutation.quantity),
            isZeroConfirmed: mutation.isZeroConfirmed,
            notes: mutation.notes,
            syncVersion: mutation.syncVersion,
          },
        },
      )
      return { kind: 'updateItem', item }
    }
    case 'createItem': {
      // Backend `POST /capture/items` returns
      // `CreateItemResponse = { created: boolean; item: CaptureItemRow }`
      // — unwrap the inner item so the room screen's reconciler
      // (item-changed event handler) sees a row with `.id` and
      // `.quantity` at the top level. Without this unwrap,
      // `isCaptureItemRow(event.item)` returns false on the wrapper
      // object, the handler bails, and the user has to refresh the
      // page to see the asset they just added.
      const response = await fetchImpl<{
        created: boolean
        item: CaptureItemRow
      }>(`/capture/items`, {
        method: 'POST',
        json: {
          sessionId: mutation.sessionId,
          spaceId: mutation.spaceId,
          assetTypeId: mutation.assetTypeId,
          quantity: canonicalizeForUpload(mutation.quantity),
          isZeroConfirmed: mutation.isZeroConfirmed,
          notes: mutation.notes,
        },
      })
      return { kind: 'createItem', item: response.item }
    }
    case 'createSpace': {
      const spaceWithItems = await fetchImpl<SpaceWithItems>(
        `/capture/sessions/${encodeURIComponent(mutation.sessionId)}/spaces`,
        {
          method: 'POST',
          json: {
            floorId: mutation.floorId,
            roomTypeId: mutation.roomTypeId,
            // Optional surveyor-supplied room name (FFR2). Forwarded only
            // when present; the backend trims, caps at 255, and
            // normalises blank → null. Omitting the key entirely when
            // undefined keeps the body identical to the pre-alias shape
            // for callers that don't set one.
            ...(mutation.alias !== undefined
              ? { alias: mutation.alias }
              : {}),
          },
        },
      )
      const { items, ...space } = spaceWithItems
      return { kind: 'createSpace', space, items }
    }
    case 'createSession': {
      // REST fallback: no client id is sent — the backend's idempotent-
      // open semantics still apply (returns the existing active session
      // if one exists for the user + scope). The PowerSync adapter
      // (capture-client-powersync-create-session.ts) is the only caller
      // that supplies an id; this REST path is exercised by callers
      // running with the PowerSync flag off OR (in production) by the
      // initial render before the PowerSync client is wired in.
      const session = await fetchImpl<SessionSummary>(`/capture/sessions`, {
        method: 'POST',
        json: {
          siteId: mutation.siteId,
          buildingId: mutation.buildingId,
        },
      })
      return { kind: 'createSession', session }
    }
    case 'completeSession': {
      const session = await fetchImpl<SessionSummary>(
        `/capture/sessions/${encodeURIComponent(mutation.sessionId)}/complete`,
        {
          method: 'PUT',
          json: { syncVersion: mutation.syncVersion },
        },
      )
      return { kind: 'completeSession', session }
    }
    case 'reopenSession': {
      const session = await fetchImpl<SessionSummary>(
        `/capture/sessions/${encodeURIComponent(mutation.sessionId)}/reopen`,
        {
          method: 'PUT',
          json: { syncVersion: mutation.syncVersion },
        },
      )
      return { kind: 'reopenSession', session }
    }
    case 'cancelSession': {
      // DELETE /capture/sessions/:id?syncVersion=N — cancels the
      // session, cascade-deletes captured items via the FK, and
      // recomputes the building's status from remaining sessions
      // (any remaining completed → 'complete'; otherwise → 'not_started').
      // Returns the new building status so the UI can update without a
      // round-trip refetch.
      //
      // 404 idempotence: if the server reports the session is already
      // gone, treat that as success. The user's intent ("cancel this
      // session") is satisfied either way, and surfacing the 404 as a
      // scary modal led admins to retry into more 404s when their
      // local PowerSync mirror was briefly out of step with the server
      // (observed on dev — first cancel succeeded, but the cancel
      // button stayed visible until sync caught up; subsequent
      // clicks 404'd with "capture session <UUID> not found"). The
      // hook's local-mirror eviction still runs on this success path,
      // so any stale local row is cleared. buildingStatus is null
      // because the server didn't tell us — callers already treat it
      // as non-authoritative and rely on the buildings sync stream.
      try {
        const response = await fetchImpl<{
          id: string
          buildingId: string | null
          buildingStatus: BuildingStatus | null
        }>(
          `/capture/sessions/${encodeURIComponent(mutation.sessionId)}?syncVersion=${mutation.syncVersion}`,
          { method: 'DELETE' },
        )
        return {
          kind: 'cancelSession',
          sessionId: response.id,
          buildingId: response.buildingId,
          buildingStatus: response.buildingStatus,
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return {
            kind: 'cancelSession',
            sessionId: mutation.sessionId,
            buildingId: null,
            buildingStatus: null,
          }
        }
        throw err
      }
    }
    case 'deleteSpace': {
      // DELETE /capture/sessions/:sessionId/spaces/:spaceId — field
      // delete of a room (ADR-015). Hard-deletes the room's
      // capture_items then the spaces row server-side and recomputes the
      // building's status, returned so the UI can flip the building badge
      // without a refetch.
      //
      // 404 idempotence: if the server reports the room is already gone
      // (a duplicate confirm, or a concurrent delete), treat that as
      // success — the surveyor's intent ("remove this room") is met
      // either way. buildingStatus is null on this branch because the
      // server didn't recompute one; callers treat it as non-
      // authoritative and rely on the buildings sync stream / their own
      // optimistic removal.
      try {
        const response = await fetchImpl<{
          spaceId: string
          buildingId: string | null
          buildingStatus: BuildingStatus | null
        }>(
          `/capture/sessions/${encodeURIComponent(
            mutation.sessionId,
          )}/spaces/${encodeURIComponent(mutation.spaceId)}`,
          { method: 'DELETE' },
        )
        return {
          kind: 'deleteSpace',
          spaceId: response.spaceId,
          buildingId: response.buildingId,
          buildingStatus: response.buildingStatus,
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return {
            kind: 'deleteSpace',
            spaceId: mutation.spaceId,
            buildingId: null,
            buildingStatus: null,
          }
        }
        throw err
      }
    }
    case 'updateSpaceAlias': {
      // PUT /capture/sessions/:sessionId/spaces/:spaceId/alias — field
      // edit of a room's alias (FFR2). Sends ONLY the alias (2-state
      // set/clear); the backend trims, caps at 255, and normalises
      // blank → null, returning the value it persisted.
      //
      // 404 idempotence: if the server reports the room is already gone
      // (a concurrent delete, or a local mirror briefly out of step),
      // treat that as success — mirrors deleteSpace's 404 carve-out. The
      // surveyor's intent is moot if the room no longer exists; surfacing
      // the 404 as an error modal would be confusing. We echo the
      // intended alias back so the optimistic UI stays consistent.
      try {
        const response = await fetchImpl<{
          spaceId: string
          alias: string | null
        }>(
          `/capture/sessions/${encodeURIComponent(
            mutation.sessionId,
          )}/spaces/${encodeURIComponent(mutation.spaceId)}/alias`,
          {
            method: 'PUT',
            json: { alias: mutation.alias },
          },
        )
        return {
          kind: 'updateSpaceAlias',
          spaceId: response.spaceId,
          alias: response.alias,
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return {
            kind: 'updateSpaceAlias',
            spaceId: mutation.spaceId,
            alias: mutation.alias,
          }
        }
        throw err
      }
    }
    case 'createFieldAddedVariant': {
      // Backend `POST /capture/field-additions` returns
      // `CreateFieldAddedResponse = { created: boolean; assetType: ... }`
      // — unwrap the inner assetType so callers (CreateVariantForm,
      // event subscribers) receive a shape that has `.id` at the top
      // level. Without this unwrap, `extractId(result.assetType)`
      // returns null and the form surfaces "Unexpected response from
      // server" even on a clean 201.
      const response = await fetchImpl<{
        created: boolean
        assetType: unknown
      }>(`/capture/field-additions`, {
        method: 'POST',
        json: {
          subSystemId: mutation.subSystemId,
          name: mutation.name,
          unitOfMeasure: mutation.unitOfMeasure,
          groupingConvention: mutation.groupingConvention,
        },
      })
      return {
        kind: 'createFieldAddedVariant',
        assetType: response.assetType,
      }
    }
  }
}
